import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Platform detection
const isDesktop = typeof window !== 'undefined' && '__TAURI__' in window;

// Declare gtag as a global function
declare global {
  interface Window {
    gtag: (
      command: string,
      action: string,
      params?: Record<string, unknown> | string
    ) => void;
    dataLayer: unknown[];
  }
}

/**
 * Hook to track page views and custom events with Google Analytics
 * Automatically disabled in desktop environments for privacy
 */
export const useAnalytics = () => {
  const location = useLocation();

  // Track page views (only in web browsers, not desktop)
  useEffect(() => {
    if (isDesktop) {
      console.log('[Analytics] Disabled in desktop environment');
      return;
    }
    
    if (typeof window.gtag !== 'undefined') {
      window.gtag('config', 'G-JKMMMPJ41P', {
        page_path: location.pathname + location.search
      });
      console.log('[Analytics] Page view tracked:', location.pathname);
    }
  }, [location]);

  // Function to track custom events
  const trackEvent = (
    eventName: string,
    eventParams?: Record<string, unknown>
  ) => {
    if (isDesktop) {
      console.log('[Analytics] Event skipped in desktop environment:', eventName);
      return;
    }
    
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', eventName, eventParams);
      console.log('[Analytics] Event tracked:', eventName, eventParams);
    } else {
      console.warn('[Analytics] gtag not available for event:', eventName);
    }
  };

  // Function to track subscription events
  const trackSubscription = (tier: string, interval: 'monthly' | 'yearly') => {
    trackEvent('subscription_started', {
      tier,
      billing_interval: interval,
      value: interval === 'monthly' ? 4.99 : 49.99,
      currency: 'USD'
    });
  };

  // Function to track errors
  const trackError = (errorMessage: string, errorSource: string) => {
    trackEvent('app_error', {
      error_message: errorMessage,
      error_source: errorSource
    });
  };

  // Function to track user actions
  const trackAction = (actionName: string, actionCategory: string, actionValue?: number) => {
    trackEvent('user_action', {
      action_name: actionName,
      action_category: actionCategory,
      action_value: actionValue
    });
  };

  return {
    trackEvent,
    trackSubscription,
    trackError,
    trackAction
  };
};
